import React, {useEffect, useRef, useState} from "react";
import {getData} from '../../requests/CRUD';
import {TEAM} from '../../consts/tables';

const POLL_INTERVAL_MS = 2000;

/** Once the run is done only Kore's queue is left to watch, which drains slowly. */
const KORE_POLL_INTERVAL_MS = 5000;

/** A finished upload stays on the tab this long, so a refresh still shows how it ended. */
const SHOW_FINISHED_FOR_MS = 24 * 60 * 60 * 1000;

const ACCEPTED = /\.(csv|xlsx)$/i;

const FINISHED = ['completed', 'failed', 'cancelled'];

function percentDone(run) {
    const total = (run.notes || {}).total_rows;

    return total ? Math.min(100, Math.round((run.processed / total) * 100)) : 0;
}

function isRunning(run) {
    return run && FINISHED.indexOf(run.status) === -1;
}

function finishedRecently(run) {
    return run.finished_at && Date.now() - new Date(run.finished_at).getTime() < SHOW_FINISHED_FOR_MS;
}

function startedAt(run) {
    return run.started_at ? new Date(run.started_at).toLocaleString() : '';
}

function pollErrorText(err) {
    return err && err.status === 401
        ? 'Your session has ended. Sign in again to follow the upload; it carries on without you.'
        : 'The upload’s status could not be read (' + ((err && err.message) || 'no answer') + '). Refresh to try again.';
}

function memberName(member) {
    return [member.FIRST_NAME, member.LAST_NAME].filter(Boolean).join(' ').trim();
}

export function noteLine(note) {
    return note.level
        ? `${note.column} “${note.value}” ${note.text}`
        : note.text;
}

/** The line's first note names what it is about: the place, or the person. */
export function subjectLine(item) {
    const first = (item.notes || [])[0] || {};

    return [first.value, first.text].filter(Boolean).join(', ');
}

function csvCell(value) {
    const text = value === null || value === undefined ? '' : String(value);

    return /[",\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}

/** Every line of a run, a page at a time, oldest first. */
async function allItems(api, runId) {
    const items = [];
    let page = 1;
    let lastPage = 1;

    do {
        const envelope = await api.items(runId, page);
        items.push(...(envelope.data || []));
        lastPage = ((envelope.meta || {}).pagination || {}).last_page || 1;
        page++;
    } while (page <= lastPage);

    return items.sort((a, b) => Number(a.external_id) - Number(b.external_id));
}

export const Stat = ({value, label, tone}) => (
    <div className={'stat' + (tone ? ' stat-' + tone : '')}>
        <span className='stat-number'>{value || 0}</span>
        <span className='stat-label'>{label}</span>
    </div>
);

function resultHeader(run, dryRun) {
    if (run.status === 'cancelled') return dryRun ? 'Test Run Cancelled' : 'Upload Cancelled';
    if (run.status === 'failed') return dryRun ? 'Test Run Stopped' : 'Upload Stopped';

    return dryRun ? 'Test Run Complete — Nothing Was Written' : 'Upload Complete';
}

function resultTone(run) {
    if (run.status === 'cancelled') return 'result-warning';

    return run.status === 'failed' ? 'result-error' : 'result-success';
}

/**
 * An upload that runs on the server a slice at a time — the Database and
 * Contacts tabs. It has a test run that writes nothing, picks up where it is
 * when the tab opens again, and offers Continue when it stopped. Each tab
 * brings its own words, counts and report columns.
 */
const SlicedUploadTab = ({api, title, instructions, stats, reportColumns, reportRow, canCancel}) => {
    const fileInput = useRef(null);
    const pollTimer = useRef(null);
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [members, setMembers] = useState([]);
    const [authorId, setAuthorId] = useState('');
    const [run, setRun] = useState(null);
    const [rows, setRows] = useState({items: [], total: 0});
    const [showRows, setShowRows] = useState(false);
    const [reportBusy, setReportBusy] = useState(false);
    const [actionError, setActionError] = useState('');
    const [pollError, setPollError] = useState('');
    const [korePending, setKorePending] = useState(0);

    useEffect(() => {
        getData({
            tableCode: TEAM,
            options: {
                column: ['ARCIMEDES_ROW_ID', 'FIRST_NAME', 'LAST_NAME', 'STATUS_CODE'],
                limit: {count: 1000, offset: 0},
            },
        }).then((res) => {
            const active = (res && res.success && Array.isArray(res.data) ? res.data : [])
                .filter((member) => memberName(member) !== '' && member.STATUS_CODE !== 'inactive')
                .sort((a, b) => memberName(a).localeCompare(memberName(b)));

            setMembers(active);
        });

        // The upload runs on the server, so the tab opened again picks it up where it is.
        api.latest().then(({run: latest, korePending: pending}) => {
            if (!latest || (!isRunning(latest) && !finishedRecently(latest))) return;

            setRun(latest);
            setKorePending(pending);

            if (isRunning(latest)) {
                poll(latest.id);
            } else {
                finish(latest);
                watchKore(latest.id, pending);
            }
        }).catch((err) => setPollError(pollErrorText(err)));

        return () => clearInterval(pollTimer.current);
    }, []);

    const chooseFile = (event) => {
        const chosen = event.target.files[0];
        event.target.value = '';

        if (!chosen) return;

        if (!ACCEPTED.test(chosen.name)) {
            setFile(null);
            setFileError('Wrong file format! Supported file types: .csv, .xlsx');

            return;
        }

        const reader = new FileReader();
        reader.addEventListener('load', (loaded) => {
            const dataUrl = loaded.target.result;
            setFileError('');
            setFile({name: chosen.name, encoded: dataUrl.slice(dataUrl.indexOf('base64,') + 'base64,'.length)});
        });
        reader.readAsDataURL(chosen);
    };

    const finish = (status) => {
        api.rows(status.id)
            .then((envelope) => setRows({items: envelope.data || [], total: (envelope.meta || {}).reviewable_total || 0}))
            .catch(() => setRows({items: [], total: 0}));
    };

    /** Kore's queue after the run: the upload is whole only once it is empty. */
    const watchKore = (id, pending) => {
        clearInterval(pollTimer.current);

        if (!pending) return;

        pollTimer.current = setInterval(() => {
            api.status(id).then(({korePending: left}) => {
                setKorePending(left);
                setPollError('');

                if (!left) clearInterval(pollTimer.current);
            }).catch((err) => setPollError(pollErrorText(err)));
        }, KORE_POLL_INTERVAL_MS);
    };

    const poll = (id) => {
        clearInterval(pollTimer.current);

        pollTimer.current = setInterval(() => {
            api.status(id).then(({run: status, korePending: pending}) => {
                setRun(status);
                setKorePending(pending);
                setPollError('');

                if (!isRunning(status)) {
                    finish(status);
                    watchKore(id, pending);
                }
            }).catch((err) => setPollError(pollErrorText(err)));
        }, POLL_INTERVAL_MS);
    };

    const start = (dryRun) => {
        setRun({status: 'uploading', processed: 0, notes: {}, mode: dryRun ? 'dry_run' : 'write'});
        setRows({items: [], total: 0});
        setPollError('');
        setKorePending(0);

        api.start(file.name, file.encoded, dryRun, authorId ? Number(authorId) : null)
            .then((started) => {
                setRun(started);
                poll(started.id);
            })
            .catch((err) => {
                setRun(null);
                setFileError((err && err.message) || 'Unspecified error');
            });
    };

    const resume = () => {
        setActionError('');

        api.resume(run.id)
            .then((resumed) => {
                setRun(resumed);
                setRows({items: [], total: 0});
                poll(resumed.id);
            })
            .catch((err) => setActionError((err && err.message) || 'Unspecified error'));
    };

    const cancel = () => {
        if (!window.confirm('Stop this upload? The rows it has already done stay done.')) return;

        setActionError('');

        api.cancel(run.id)
            .then(({run: cancelled, korePending: pending}) => {
                clearInterval(pollTimer.current);
                setRun(cancelled);
                setKorePending(pending);
                finish(cancelled);
                watchKore(cancelled.id, pending);
            })
            .catch((err) => setActionError((err && err.message) || 'Unspecified error'));
    };

    const reset = () => {
        clearInterval(pollTimer.current);
        setRun(null);
        setFile(null);
        setFileError('');
        setActionError('');
        setPollError('');
        setKorePending(0);
        setRows({items: [], total: 0});
        setShowRows(false);
    };

    const downloadReport = async () => {
        setReportBusy(true);

        try {
            const items = await allItems(api, run.id);
            const lines = [reportColumns.join(',')].concat(items.map((item) => reportRow(item).map(csvCell).join(',')));
            const url = URL.createObjectURL(new Blob([lines.join('\n')], {type: 'text/csv;charset=utf-8;'}));
            const link = document.createElement('a');

            link.href = url;
            link.download = (run.mode === 'dry_run' ? 'Test run - ' : 'Upload - ') + run.label.replace(/\.(csv|xlsx)$/i, '') + '.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            setActionError((err && err.message) || 'The report could not be prepared');
        } finally {
            setReportBusy(false);
        }
    };

    const running = isRunning(run);
    const done = run && !running;
    const dryRun = run && run.mode === 'dry_run';
    const notes = (run && run.notes) || {};

    return (
        <div className='sliced-upload-tab'>
            <div className='upload-title-row'>
                <div className='upload-file-title'>{title}</div>
            </div>

            <div className='upload-instructions'>{instructions}</div>

            <input
                type='file'
                accept='.csv,.xlsx'
                name='file'
                ref={fileInput}
                onChange={chooseFile}
                style={{display: 'none'}}
            />

            {!run &&
                <div>
                    <div className='upload-file-details'>
                        {file
                            ? <React.Fragment>
                                <div className='upload-file-text'>{file.name}</div>
                                <div
                                    onClick={() => setFile(null)}
                                    className='button delete'
                                    style={{backgroundImage: 'url("' + arc.path.media + 'delete.svg")'}}
                                />
                            </React.Fragment>
                            : <button type='button' className='button' onClick={() => fileInput.current.click()}>Browse...</button>
                        }
                    </div>

                    {fileError && <div className='upload-file-text sliced-upload-error'>{fileError}</div>}
                    {pollError && <div className='upload-file-text sliced-upload-error'>{pollError}</div>}

                    <label className='sliced-upload-author'>
                        <span>Kore notes written as</span>
                        <select value={authorId} onChange={(event) => setAuthorId(event.target.value)}>
                            <option value=''>Kore's default account</option>
                            {members.map((member) => (
                                <option key={member.ARCIMEDES_ROW_ID} value={member.ARCIMEDES_ROW_ID}>{memberName(member)}</option>
                            ))}
                        </select>
                    </label>

                    <div className='action-row'>
                        <button type='button' className='button ghost' disabled={!file} onClick={() => start(true)}>
                            Test Run
                        </button>
                        <button type='button' className='button submit' disabled={!file} onClick={() => start(false)}>
                            Upload
                        </button>
                    </div>
                </div>
            }

            {running &&
                <div className='progress-section'>
                    <div className='progress-label'>
                        {run.status === 'uploading' ? 'Uploading file...' : (dryRun ? 'Test run' : 'Uploading')} {percentDone(run)}%
                        {run.label && <span className='progress-file'>{run.label} — started {startedAt(run)}</span>}
                    </div>
                    <div className='progress-bar-track'>
                        <div className='progress-bar-fill' style={{width: percentDone(run) + '%'}}/>
                    </div>
                    {pollError && <div className='upload-file-text sliced-upload-error'>{pollError}</div>}
                    {actionError && <div className='upload-file-text sliced-upload-error'>{actionError}</div>}
                    <div className='action-row'>
                        {canCancel && run.id &&
                            <button type='button' className='button' onClick={cancel}>
                                {dryRun ? 'Cancel Test Run' : 'Cancel Upload'}
                            </button>
                        }
                        <button type='button' className='button ghost' onClick={reset}>Upload Another File</button>
                    </div>
                </div>
            }

            {done &&
                <div className='result-section'>
                    <div className={'result-header ' + resultTone(run)}>
                        {resultHeader(run, dryRun)}
                        {run.label && <span className='result-file'> — {run.label}</span>}
                    </div>

                    {stats(run, notes, dryRun)}

                    {(notes.unknown_columns || []).length > 0 &&
                        <div className='result-details result-hint'>
                            These columns are not part of iCLIMB and were ignored:{' '}
                            <strong>{notes.unknown_columns.join(', ')}</strong>.
                        </div>
                    }

                    {run.error && <div className={'result-details ' + (run.status === 'cancelled' ? 'result-hint' : 'details-error')}>{run.error}</div>}
                    {actionError && <div className='result-details details-error'>{actionError}</div>}
                    {pollError && <div className='result-details details-error'>{pollError}</div>}
                    {!dryRun && run.status !== 'failed' &&
                        <div className='result-details result-hint'>
                            {korePending > 0
                                ? <React.Fragment>Kore is still being written to: <strong>{korePending}</strong> updates queued.</React.Fragment>
                                : 'Every Kore update is written.'}
                        </div>
                    }

                    {rows.total > 0 &&
                        <div className='result-details'>
                            <div className='details-toggle' onClick={() => setShowRows(!showRows)}>
                                Rows To Review ({rows.total})
                            </div>
                            {showRows &&
                                <div className='details-list'>
                                    {rows.items.map((row) => (
                                        <div key={row.id} className={'details-item' + (row.outcome === 'failed' ? ' details-error-item' : '')}>
                                            <div>
                                                Row {row.external_id}: {subjectLine(row)} — {row.reason}
                                            </div>
                                            {(row.notes || []).slice(1).map((note, index) => (
                                                <div key={index} className={'details-issue' + (note.level ? ' details-issue--' + note.level : '')}>
                                                    {noteLine(note)}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                    {rows.total > rows.items.length &&
                                        <div className='details-item'>...and {rows.total - rows.items.length} more in the report</div>
                                    }
                                </div>
                            }
                        </div>
                    }

                    <div className='action-row'>
                        {dryRun && run.status === 'completed' &&
                            <button type='button' className='button' onClick={() => start(false)}>Upload</button>
                        }
                        {run.status === 'failed' &&
                            <button type='button' className='button' onClick={resume}>Continue</button>
                        }
                        <button type='button' className='button ghost' disabled={reportBusy} onClick={downloadReport}>
                            {reportBusy ? 'Preparing Report...' : 'Download Report'}
                        </button>
                        <button type='button' className='button ghost' onClick={reset}>Upload Another File</button>
                    </div>
                </div>
            }
        </div>
    );
};

export default SlicedUploadTab;
